/**
 * api.ts — V2.0 API client
 * Tutti i fetch verso /api/* con autenticazione JWT da localStorage.
 */

import type {
  ContentItem,
  GraphSlice,
  GraphEntity,
  GraphRelation,
  GraphSnippet,
  GraphDocument,
  GenerateReportRequest,
  GeneratedReport,
  GeneratedArticle,
  InboxItem,
  InboxCounts,
  Integration,
  IntegrationPlatform,
  PublishTargets,
  PublishResult,
} from '../types/v2';
import type {
  AnalystSettingsView, AnalystSummary, ApiEnvelope, ConversationView, DatasetProfileView, DatasetRowView, MetricAvailability, RunEventView, RunListItem,
  RunView, SearchCoverage, SearchOption, StartRunBody, StoryView, UploadedExport,
} from '../shared/analyst/api';

// Re-export types consumed by graph components
export type { GraphEntity, GraphRelation, GraphSnippet, GraphDocument, GraphSlice, ContentItem };

const BASE = '/api';
const TOKEN_KEY = 'auth_token';

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) || '';
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers as Record<string, string> | undefined ?? {}),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText })) as { message?: string; error?: string };
    throw new Error(body.message || body.error || `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ─── Content Items ────────────────────────────────────────────────────────────

export function listContentItems(): Promise<ContentItem[]> {
  return apiFetch<{ items: ContentItem[] }>('/content-items').then((d) => d.items);
}

export function getContentItemStatus(id: string): Promise<ContentItem> {
  return apiFetch<ContentItem>(`/content-items/${id}`);
}

export function ingestContentItem(
  file: File,
  title?: string
): Promise<{ id: string; is_duplicate: boolean; pipeline_status: string; message: string }> {
  const formData = new FormData();
  formData.append('file', file);
  if (title) formData.append('title', title);

  const token = getToken();
  return fetch(`${BASE}/content-items/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: res.statusText })) as { message?: string; error?: string };
      throw new Error(body.message || body.error || `HTTP ${res.status}`);
    }
    return res.json() as Promise<{ id: string; is_duplicate: boolean; pipeline_status: string; message: string }>;
  });
}

export function ingestUrl(
  url: string
): Promise<{ inbox_id: string; title: string; already_exists?: boolean; message: string }> {
  return apiFetch('/content-items/url', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

// ─── Research Inbox ───────────────────────────────────────────────────────────

export interface InboxListParams {
  status?: 'pending' | 'approved' | 'rejected';
  limit?:  number;
  offset?: number;
}

export function listInboxItems(params: InboxListParams = {}): Promise<{ items: InboxItem[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.limit)  qs.set('limit',  String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  return apiFetch<{ data: InboxItem[]; meta: { total: number } }>(`/research/inbox?${qs.toString()}`)
    .then((d) => ({ items: d.data, total: d.meta.total }));
}

export function getInboxCounts(): Promise<InboxCounts> {
  return apiFetch<{ data: InboxCounts }>('/research/inbox/counts').then((d) => d.data);
}

export function approveInboxItem(id: string): Promise<{ content_item_id: string }> {
  return apiFetch<{ data: { content_item_id: string } }>(`/research/inbox/${id}/approve`, { method: 'POST' })
    .then((d) => d.data);
}

export function rejectInboxItem(id: string): Promise<void> {
  return apiFetch(`/research/inbox/${id}/reject`, { method: 'POST' }).then(() => undefined);
}

export function approveInboxBatch(ids: string[]): Promise<{ approved: number; errors: number }> {
  return apiFetch<{ data: { approved: number; errors: number } }>('/research/inbox/approve-batch', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  }).then((d) => d.data);
}

export function deleteInboxItem(id: string): Promise<void> {
  return apiFetch(`/research/inbox/${id}`, { method: 'DELETE' }).then(() => undefined);
}

export function getInboxItem(id: string): Promise<InboxItem> {
  return apiFetch<{ data: InboxItem }>(`/research/inbox/${id}`).then((d) => d.data);
}

export interface WebSearchParams {
  query:       string;
  maxResults?: number;
  topic?:      'news' | 'general';
  days?:       number;
}

export interface WebSearchResult {
  saved:      number;
  totalFound: number;
  duplicates: number;
  skipped:    number;
  message:    string;
}

interface BraveSearchData {
  totalFound: number;
  staged:     number;
  duplicates: number;
  skipped:    number;
  errors:     string[];
}

export function runWebSearch(params: WebSearchParams): Promise<WebSearchResult> {
  const { query, ...options } = params;
  return apiFetch<{ message: string; data: BraveSearchData }>('/research/search', {
    method: 'POST',
    body: JSON.stringify({ query, options }),
  }).then((d) => ({
    saved:      d.data.staged,
    totalFound: d.data.totalFound,
    duplicates: d.data.duplicates ?? 0,
    skipped:    d.data.skipped    ?? 0,
    message:    d.message,
  }));
}

// ─── Pulsar Media Intelligence ─────────────────────────────────────────────────

export interface PulsarSearch {
  id:             string;
  name:           string;
  searchHash:     string;
  totalContents:  number;
  startDate:      string;
  status:         string;
  realtimeStatus: string;
  type:           string;
}

export function listPulsarSearches(): Promise<{ totalCount: number; searches: PulsarSearch[] }> {
  return apiFetch<{ data: { totalCount: number; searches: PulsarSearch[] } }>('/research/pulsar/searches')
    .then((d) => d.data);
}

export interface CreatePulsarSearchParams {
  name:              string;
  booleanExpression: string;
  categories:        string[];
}

export function createPulsarSearch(params: CreatePulsarSearchParams): Promise<PulsarSearch> {
  return apiFetch<{ data: PulsarSearch }>('/research/pulsar/searches', {
    method: 'POST',
    body: JSON.stringify(params),
  }).then((d) => d.data);
}

export interface PreviewPulsarImportParams {
  searchId:   string;
  categories: string[];
  startDate:  string;
  endDate:    string;
}

export interface PulsarHistoricHandle {
  historicId: number;
  status:     string;
}

/** Fase 1: crea l'historic. Ritorna subito — fare polling con getPulsarPreviewStatus(). */
export function previewPulsarImport(params: PreviewPulsarImportParams): Promise<PulsarHistoricHandle> {
  return apiFetch<{ data: PulsarHistoricHandle }>('/research/pulsar/preview', {
    method: 'POST',
    body: JSON.stringify(params),
  }).then((d) => d.data);
}

export interface PulsarPreviewStatus {
  historicId:    number;
  status:        string;
  previewResult: number | null;
  sampleSize:    number | null;
  ready:         boolean;
}

/** Polling: interroga lo stato dell'historic finché ready non è true (può richiedere 30-40s+ lato Pulsar). */
export function getPulsarPreviewStatus(historicId: number, searchId: string): Promise<PulsarPreviewStatus> {
  return apiFetch<{ data: PulsarPreviewStatus }>(
    `/research/pulsar/preview/${historicId}?searchId=${encodeURIComponent(searchId)}`
  ).then((d) => d.data);
}

export interface PulsarSourceTier {
  key:         string;
  label:       string;
  domainCount: number;
}

/** Tier editoriali del registro locale curato (non richiede l'account Pulsar). */
export function listPulsarSourceTiers(): Promise<PulsarSourceTier[]> {
  return apiFetch<{ data: PulsarSourceTier[] }>('/research/pulsar/source-tiers').then((d) => d.data);
}

export interface NumericRange {
  min?: number;
  max?: number;
}

export interface PulsarImportFilters {
  /** Chiavi del registro tier locale (es. ['B','C']) — risolte in FilterInput.domains lato server. */
  sourceTiers?:       string[];
  /** NewsSubtypesEnum — solo per canale ONLINE_NEWS. */
  newsSubtypes?:      string[];
  /** Reach di articoli/media — news, TV, radio, podcast. */
  mediaReach?:        NumericRange;
  /** Engagement — canali social/community. */
  engagements?:       NumericRange;
  /** Follower dell'autore — canali social/community. */
  followers?:         NumericRange;
  /** SentimentEnum (POSITIVE/NEGATIVE/NEUTRAL). */
  sentiments?:        string[];
  /** ReliabilityLabelsEnum (CREDIBLE/NON_CREDIBLE/SATIRE/USER_GENERATED_CONTENT). */
  credibilityLabels?: string[];
}

export interface RunPulsarImportParams {
  historicId: number;
  searchId:   string;
  searchHash: string;
  dateFrom:   string;
  dateTo:     string;
  sources?:   string[];
  limit?:     number;
  /** Applicati solo in import (non riflessi nella stima di anteprima — Pulsar non lo supporta lì). */
  filters?:   PulsarImportFilters;
}

export interface PulsarImportResult {
  totalFetched: number;
  imported:     number;
  duplicates:   number;
  skipped:      number;
  errors:       string[];
}

/** Fase 2: lancia il recupero effettivo e importa in inbox (da approvare poi normalmente). */
export function runPulsarImport(params: RunPulsarImportParams): Promise<PulsarImportResult> {
  return apiFetch<{ message: string; data: PulsarImportResult }>('/research/pulsar/import', {
    method: 'POST',
    body: JSON.stringify(params),
  }).then((d) => d.data);
}

// ─── Graph ────────────────────────────────────────────────────────────────────

export function getGraphSlice(
  focusType: 'entity' | 'relation' | 'query',
  focus: Record<string, string>,
  options: Record<string, unknown> = {}
): Promise<GraphSlice> {
  return apiFetch<{ graphSlice: GraphSlice }>('/reports/outline', {
    method: 'POST',
    body: JSON.stringify({ focusType, focus, options }),
  }).then((d) => d.graphSlice);
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export function generateReport(request: GenerateReportRequest): Promise<GeneratedReport> {
  return apiFetch('/reports/generate', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function getReport(id: number): Promise<GeneratedArticle> {
  return apiFetch(`/reports/${id}`);
}

export function listReports(): Promise<GeneratedArticle[]> {
  return apiFetch<{ reports: GeneratedArticle[] }>('/reports').then((d) => d.reports);
}

export function getReportEntities(q: string): Promise<string[]> {
  return apiFetch<{ entities: string[] }>(`/reports/entities?q=${encodeURIComponent(q)}`).then((d) => d.entities);
}

// ─── Publish ──────────────────────────────────────────────────────────────────

export function publishArticle(articleId: number, targets: PublishTargets): Promise<PublishResult> {
  return apiFetch(`/publish/${articleId}`, {
    method: 'POST',
    body: JSON.stringify({ targets }),
  });
}

export function getPublishStatus(
  articleId: number
): Promise<{ published: boolean; platforms: Array<{ platform: string; url: string; postId: string; published_at: string }> }> {
  return apiFetch<{ success: boolean; data: { published: boolean; platforms: Array<{ platform: string; url: string; postId: string; published_at: string }> } }>(
    `/publish/${articleId}/status`
  ).then((d) => d.data);
}

// ─── Settings / Integrations ──────────────────────────────────────────────────

export function listIntegrations(): Promise<Integration[]> {
  return apiFetch<{ success: boolean; data: Integration[] }>('/settings/integrations').then((d) => d.data);
}

export function saveIntegration(
  platform: IntegrationPlatform,
  credentials: Record<string, string>,
  config?: Record<string, unknown>
): Promise<void> {
  return apiFetch(`/settings/integrations/${platform}`, {
    method: 'PUT',
    body: JSON.stringify({ credentials, config }),
  });
}

export function testIntegration(
  platform: IntegrationPlatform
): Promise<{ success: boolean; message: string; userName?: string; pageName?: string }> {
  return apiFetch(`/settings/integrations/${platform}/test`, { method: 'POST' });
}

export function deleteIntegration(platform: IntegrationPlatform): Promise<void> {
  return apiFetch(`/settings/integrations/${platform}`, { method: 'DELETE' });
}

// ─── Agente Analista (/api/analyst) ───────────────────────────────────────────


export type {
  AnalystSettingsView, AnalystSummary, ConversationView, DatasetProfileView, DatasetRowView, MetricAvailability, RunEventView, RunListItem, RunView,
  SearchCoverage, SearchOption, StartRunBody, StoryView, UploadedExport,
};

/** Errore dell'API dell'analista con il codice restituito dal server (es. "too_many_active_runs"). */
export class AnalystApiError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) {
    super(message);
    this.name = 'AnalystApiError';
  }
}

async function analystFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const isForm = init.body instanceof FormData;
  const res = await fetch(`${BASE}/analyst${path}`, {
    ...init,
    headers: {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers as Record<string, string> | undefined ?? {}),
    },
  });
  let body: ApiEnvelope<T>;
  try {
    body = await res.json() as ApiEnvelope<T>;
  } catch {
    throw new AnalystApiError(`Risposta non valida dal server (HTTP ${res.status})`, 'invalid_response', res.status);
  }
  if (!res.ok || !body.success || body.data === undefined) {
    throw new AnalystApiError(body.error ?? `HTTP ${res.status}`, body.code ?? 'error', res.status);
  }
  return body.data;
}

export const analystApi = {
  conversations: (archived = false): Promise<ConversationView[]> => analystFetch(`/conversations?limit=100&archived=${archived}`),
  updateConversation: (id: string, patch: { title?: string; pinned?: boolean; archived?: boolean }): Promise<ConversationView> =>
    analystFetch(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  conversationRuns: (id: string): Promise<RunListItem[]> => analystFetch(`/conversations/${id}/runs`),
  searches: (): Promise<SearchOption[]> => analystFetch('/searches'),
  coverage: (searchId: string, from: string, to: string): Promise<SearchCoverage> =>
    analystFetch(`/searches/${searchId}/coverage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  uploadExport: (file: File): Promise<UploadedExport> => {
    const form = new FormData();
    form.append('file', file);
    return analystFetch('/exports', { method: 'POST', body: form });
  },
  startRun: (body: StartRunBody): Promise<{ runId: string; conversationId: string }> => analystFetch('/runs', { method: 'POST', body: JSON.stringify(body) }),
  run: (id: string): Promise<RunView> => analystFetch(`/runs/${id}`),
  events: (id: string, after: number): Promise<RunEventView[]> => analystFetch(`/runs/${id}/events?after=${after}`),
  answer: (id: string, choice: 'proceed' | 'change_scope'): Promise<{ status: string }> =>
    analystFetch(`/runs/${id}/answer`, { method: 'POST', body: JSON.stringify({ choice }) }),
  cancel: (id: string): Promise<{ status: string }> => analystFetch(`/runs/${id}/cancel`, { method: 'POST' }),
  rerun: (id: string): Promise<{ runId: string; conversationId: string }> => analystFetch(`/runs/${id}/rerun`, { method: 'POST', body: '{}' }),
  datasetProfile: (id: string): Promise<DatasetProfileView> => analystFetch(`/datasets/${id}/profile`),
  metricAvailability: (id: string): Promise<MetricAvailability> => analystFetch(`/datasets/${id}/metric-availability`),
  datasetRows: (id: string, params: Record<string, string>): Promise<{ total: number; rows: DatasetRowView[] }> =>
    analystFetch(`/datasets/${id}/rows?${new URLSearchParams(params).toString()}`),
  story: (datasetId: string, storyId: string): Promise<StoryView> => analystFetch(`/datasets/${datasetId}/stories/${storyId}`),
  summary: (): Promise<AnalystSummary> => analystFetch('/summary'),
  settings: (): Promise<AnalystSettingsView> => analystFetch('/settings'),
};

/** Scarica il report come file (md o json) con l'autenticazione dell'utente. */
export async function downloadRunExport(runId: string, format: 'md' | 'json'): Promise<void> {
  const token = getToken();
  const res = await fetch(`${BASE}/analyst/runs/${runId}/export?format=${format}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new AnalystApiError(`Esportazione non riuscita (HTTP ${res.status})`, 'export_failed', res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `analisi-${runId.slice(0, 8)}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}
